import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import {
  getPurchaseOptions,
  searchPurchaseProjects,
  getPurchaseRequests,
  getPurchaseRequest,
  createPurchaseRequest,
  updatePurchaseRequest,
  submitPurchaseRequest,
  deletePurchaseRequest,
} from "../api/service";
import ApproveTab from "./purchase/ApproveTab";
import RateListTab from "./purchase/RateListTab";
import TallyTab from "./purchase/TallyTab";
import VendorsTab from "./purchase/VendorsTab";
import { AgeingSelect, ProjectFilter, RemarksPanel } from "./purchase/shared";

const TABS = [
  { id: "request", label: "Purchase Request", live: true },
  { id: "approve", label: "Approve Request", live: true },
  { id: "invoice", label: "Invoice Tally", live: true },
  { id: "rates", label: "Rate List", live: true, mastersOnly: true },
  { id: "vendors", label: "Vendors", live: true, mastersOnly: true },
];
const PAGE_SIZE = 20;
const accessoryFilter = createFilterOptions();

let keySeq = 0;
const nextKey = () => {
  keySeq += 1;
  return keySeq;
};

function blankLine(materialType, opts) {
  return {
    key: nextKey(),
    material_type: materialType,
    sub_type: "",
    accessory_item: "",
    attributes: {},
    qty: "",
    unit: (opts.units && opts.units[0] && opts.units[0].value) || "pcs",
    remarks: "",
  };
}

function fromServerLine(l) {
  return {
    key: nextKey(),
    material_type: l.material_type,
    sub_type: l.sub_type || "",
    accessory_item: l.accessory_item || "",
    attributes: l.attributes || {},
    qty: String(l.qty),
    unit: l.unit,
    remarks: l.remarks || "",
  };
}

function fieldsFor(line, opts) {
  if (line.material_type === opts.site_work_type) return opts.site_work_fields || [];
  if (line.sub_type === "accessories") {
    const item = (opts.accessory_items || []).find((a) => a.value === line.accessory_item);
    return item ? item.fields : [];
  }
  const st = (opts.sub_types || []).find((s) => s.value === line.sub_type);
  return st ? st.fields : [];
}

function lineProblems(line, opts) {
  const p = [];
  const isSite = line.material_type === opts.site_work_type;
  if (!isSite) {
    if (!line.sub_type) return ["choose a sub type"];
    if (line.sub_type === "accessories" && !line.accessory_item) return ["choose an accessory"];
  }
  fieldsFor(line, opts).forEach((f) => {
    const v = line.attributes[f.key];
    if (v === undefined || v === null || String(v).trim() === "") p.push(f.label + " is required");
  });
  if (!(parseFloat(line.qty) > 0)) p.push("enter a quantity above 0");
  if (!line.unit) p.push("choose a unit");
  return p;
}

function ProjectPicker({ value, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || (value && query === value.display)) {
      setOptions([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchPurchaseProjects(query);
        setOptions(res?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, value]);

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(o) => o?.display || ""}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={query}
      onInputChange={(_, v) => setQuery(v)}
      loading={loading}
      filterOptions={(x) => x}
      noOptionsText={query ? "No matching project under execution" : "Type project number or customer name..."}
      renderInput={(params) => <TextField {...params} placeholder="Search project..." />}
    />
  );
}

function AccessoryPicker({ items, value, disabled, onPick }) {
  const selected = items.find((i) => i.value === value) || null;
  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={items}
      value={selected}
      getOptionLabel={(o) => o.label || ""}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      filterOptions={(options, state) => {
        const found = accessoryFilter(options, state);
        const typed = state.inputValue.trim();
        if (found.length === 0 && typed) {
          return [{ value: "others", label: 'Others: "' + typed + '"', typed: typed, fields: [] }];
        }
        return found;
      }}
      onChange={(_, val) => onPick(val)}
      renderInput={(params) => <TextField {...params} placeholder="Search accessory..." />}
    />
  );
}

function FieldInput({ field, value, disabled, onChange }) {
  if (field.kind === "choice") {
    return (
      <select
        className="form-select form-select-sm"
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select...</option>
        {field.choices.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      className="form-control form-control-sm"
      disabled={disabled}
      maxLength={255}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function LineEditor({ line, index, opts, disabled, onChange, onRemove }) {
  const isSite = line.material_type === opts.site_work_type;
  const fields = fieldsFor(line, opts);
  const set = (patch) => onChange({ ...line, ...patch });
  const setAttr = (k, v) => set({ attributes: { ...line.attributes, [k]: v } });
  const pickAccessory = (val) => {
    if (!val) set({ accessory_item: "", attributes: {} });
    else if (val.typed) set({ accessory_item: "others", attributes: { name: val.typed } });
    else set({ accessory_item: val.value, attributes: {} });
  };

  return (
    <div className="border rounded p-2 mb-2">
      <div className="row g-2 align-items-end">
        <div className="col-auto small text-muted pb-2">{index + 1}.</div>
        {!isSite && (
          <div className="col-md-2">
            <label className="form-label small mb-1">Sub type</label>
            <select
              className="form-select form-select-sm"
              disabled={disabled}
              value={line.sub_type}
              onChange={(e) => set({ sub_type: e.target.value, accessory_item: "", attributes: {} })}
            >
              <option value="">Select...</option>
              {opts.sub_types.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}
        {!isSite && line.sub_type === "accessories" && (
          <div className="col-md-3">
            <label className="form-label small mb-1">Accessory</label>
            <AccessoryPicker
              items={opts.accessory_items}
              value={line.accessory_item}
              disabled={disabled}
              onPick={pickAccessory}
            />
          </div>
        )}
        {fields.map((f) => (
          <div className={f.kind === "text" ? (isSite ? "col-md-6" : "col-md-4") : "col-md-2"} key={f.key}>
            <label className="form-label small mb-1">{f.label}</label>
            <FieldInput
              field={f}
              value={line.attributes[f.key]}
              disabled={disabled}
              onChange={(v) => setAttr(f.key, v)}
            />
          </div>
        ))}
        <div className="col-md-1">
          <label className="form-label small mb-1">Qty</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="form-control form-control-sm"
            disabled={disabled}
            value={line.qty}
            onChange={(e) => set({ qty: e.target.value })}
          />
        </div>
        <div className="col-md-1">
          <label className="form-label small mb-1">Unit</label>
          <select
            className="form-select form-select-sm"
            disabled={disabled}
            value={line.unit}
            onChange={(e) => set({ unit: e.target.value })}
          >
            {opts.units.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>
        {!disabled && (
          <div className="col-auto">
            <button type="button" className="btn btn-outline-danger btn-sm" title="Remove line" aria-label="Remove line" onClick={onRemove}>
              &times;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, open, onToggle, onAdd, disabled, children }) {
  return (
    <div className="card mb-2">
      <div
        className="card-header d-flex align-items-center justify-content-between py-2"
        style={{ cursor: "pointer" }}
        onClick={onToggle}
      >
        <strong>
          {open ? "\u25BE " : "\u25B8 "}
          {title} <span className="text-muted small">({count})</span>
        </strong>
        {!disabled && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
          >
            + Add line
          </button>
        )}
      </div>
      {open && <div className="card-body py-2">{children}</div>}
    </div>
  );
}

function RequestForm({ opts, requestId, onClose }) {
  const [loading, setLoading] = useState(!!requestId);
  const [currentId, setCurrentId] = useState(requestId || null);
  const [number, setNumber] = useState("");
  const [status, setStatus] = useState("draft");
  const [project, setProject] = useState(null);
  const [grade, setGrade] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([]);
  const [openSec, setOpenSec] = useState({});
  const [saving, setSaving] = useState(false);
  const [errs, setErrs] = useState([]);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const allOpen = {};
    opts.material_types.forEach((t) => {
      allOpen[t.value] = true;
    });
    setOpenSec(allOpen);
    if (!requestId) return undefined;
    let live = true;
    getPurchaseRequest(requestId)
      .then((r) => {
        if (!live) return;
        setNumber(r.number);
        setStatus(r.status);
        setProject({ id: r.project, display: r.project_label });
        setGrade(r.material_grade);
        setRequiredBy(r.required_by || "");
        setRemarks(r.remarks || "");
        setLines((r.lines || []).map(fromServerLine));
        setLoading(false);
      })
      .catch((e) => {
        if (!live) return;
        setErrs([e.message || "Could not load request."]);
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [requestId, opts]);

  const readOnly = status !== "draft" || !opts.can_raise;

  const addLine = (type) => {
    setLines((ls) => [...ls, blankLine(type, opts)]);
    setOpenSec((o) => ({ ...o, [type]: true }));
  };
  const updateLine = (key, next) => setLines((ls) => ls.map((l) => (l.key === key ? next : l)));
  const removeLine = (key) => setLines((ls) => ls.filter((l) => l.key !== key));

  const validate = () => {
    const problems = [];
    if (!project) problems.push("Select a project.");
    if (!grade) problems.push("Select a material grade.");
    if (!lines.length) problems.push("Add at least one line.");
    opts.material_types.forEach((t) => {
      lines
        .filter((l) => l.material_type === t.value)
        .forEach((l, i) => {
          lineProblems(l, opts).forEach((p) => problems.push(t.label + " line " + (i + 1) + ": " + p));
        });
    });
    return problems;
  };

  const buildBody = () => ({
    project: project.id,
    material_grade: grade,
    required_by: requiredBy || null,
    remarks: remarks,
    lines: opts.material_types
      .flatMap((t) => lines.filter((l) => l.material_type === t.value))
      .map((l) => ({
        material_type: l.material_type,
        sub_type: l.sub_type || "",
        accessory_item: l.accessory_item || "",
        attributes: l.attributes,
        qty: l.qty,
        unit: l.unit,
        remarks: l.remarks || "",
      })),
  });

  const persist = async () => {
    const problems = validate();
    if (problems.length) {
      setErrs(problems);
      return null;
    }
    setErrs([]);
    const body = buildBody();
    const saved = currentId
      ? await updatePurchaseRequest(currentId, body)
      : await createPurchaseRequest(body);
    setCurrentId(saved.id);
    setNumber(saved.number);
    return saved;
  };

  const saveDraft = async () => {
    setSaving(true);
    setMsg("");
    try {
      const saved = await persist();
      if (saved) setMsg("Saved as " + saved.number + ".");
    } catch (e) {
      setErrs([e.message || "Save failed."]);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!window.confirm("Submit this request? It cannot be edited afterwards.")) return;
    setSaving(true);
    setMsg("");
    try {
      const saved = await persist();
      if (!saved) return;
      const done = await submitPurchaseRequest(saved.id);
      setStatus(done.status);
      setMsg(done.number + " submitted.");
    } catch (e) {
      setErrs([e.message || "Submit failed."]);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-muted p-3">Loading...</div>;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">
            {number || "New purchase request"}{" "}
            <span className={"badge " + (status === "draft" ? "bg-secondary" : "bg-success")}>{status}</span>
          </h5>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          Back to list
        </button>
      </div>

      {errs.length > 0 && (
        <div className="alert alert-danger py-2">
          <ul className="mb-0 ps-3">
            {errs.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label small mb-1">Project</label>
              <ProjectPicker value={project} onChange={setProject} disabled={readOnly} />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Material grade</label>
              <select
                className="form-select form-select-sm"
                disabled={readOnly}
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
              >
                <option value="">Select...</option>
                {opts.material_grades.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Required by (optional)</label>
              <input
                type="date"
                className="form-control form-control-sm"
                disabled={readOnly}
                value={requiredBy}
                onChange={(e) => setRequiredBy(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-1">Remarks (optional)</label>
              <input
                className="form-control form-control-sm"
                disabled={readOnly}
                maxLength={2000}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {opts.material_types.map((t) => {
        const tl = lines.filter((l) => l.material_type === t.value);
        return (
          <Section
            key={t.value}
            title={t.label}
            count={tl.length}
            open={!!openSec[t.value]}
            disabled={readOnly}
            onToggle={() => setOpenSec((o) => ({ ...o, [t.value]: !o[t.value] }))}
            onAdd={() => addLine(t.value)}
          >
            {tl.length === 0 && <div className="text-muted small">No lines yet.</div>}
            {tl.map((l, i) => (
              <LineEditor
                key={l.key}
                line={l}
                index={i}
                opts={opts}
                disabled={readOnly}
                onChange={(n) => updateLine(l.key, n)}
                onRemove={() => removeLine(l.key)}
              />
            ))}
          </Section>
        );
      })}

      {!readOnly && (
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-primary" disabled={saving} onClick={saveDraft}>
            {saving ? "Saving..." : "Save draft"}
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={submit}>
            Submit request
          </button>
        </div>
      )}
      {currentId && <RemarksPanel requestId={currentId} />}
    </div>
  );
}

function RequestList({ opts, onOpen, onNew, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState("");
  const [projectF, setProjectF] = useState(null);
  const [ageF, setAgeF] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const h = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(h);
  }, [searchInput]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = { page: page, page_size: PAGE_SIZE };
    if (statusF) params.status = statusF;
    if (search.trim()) params.search = search.trim();
    if (projectF) params.project = projectF.id;
    if (ageF) params.ageing = ageF;
    getPurchaseRequests(params)
      .then((res) => {
        if (!live) return;
        setRows(res.results || []);
        setCount(res.count || 0);
        setErr("");
      })
      .catch((e) => {
        if (live) setErr(e.message || "Could not load requests.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [page, statusF, search, projectF, ageF, refreshKey, tick]);

  const remove = async (r) => {
    if (!window.confirm("Delete draft " + r.number + "?")) return;
    try {
      await deletePurchaseRequest(r.id);
      setTick((t) => t + 1);
    } catch (e) {
      setErr(e.message || "Delete failed.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="d-flex align-items-end justify-content-between mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label className="form-label small mb-1">Search (PR number)</label>
            <input
              className="form-control form-control-sm"
              placeholder="PR-0001"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Status</label>
            <select
              className="form-select form-select-sm"
              value={statusF}
              onChange={(e) => {
                setStatusF(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
            </select>
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Project</label>
            <ProjectFilter
              value={projectF}
              onChange={(v) => {
                setProjectF(v);
                setPage(1);
              }}
            />
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Age (since submitted)</label>
            <AgeingSelect
              value={ageF}
              onChange={(v) => {
                setAgeF(v);
                setPage(1);
              }}
            />
          </div>
        </div>
        {opts.can_raise && (
          <button type="button" className="btn btn-primary btn-sm" onClick={onNew}>
            + New request
          </button>
        )}
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead>
            <tr>
              <th>Request</th>
              <th>Project</th>
              <th>Grade</th>
              <th className="text-end">Lines</th>
              <th>Status</th>
              <th className="text-end">Age</th>
              <th>Raised by</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.number}</td>
                <td>{r.project_label}</td>
                <td>{r.material_grade_label}</td>
                <td className="text-end">{(r.lines || []).length}</td>
                <td>
                  <span className={"badge " + (r.status === "draft" ? "bg-secondary" : "bg-success")}>
                    {r.status}
                  </span>
                </td>
                <td className="text-end">{r.status === "submitted" && r.age_days !== null && r.age_days !== undefined ? r.age_days + "d" : "-"}</td>
                <td>{r.created_by ? r.created_by.username : "-"}</td>
                <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                <td className="text-end">
                  <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => onOpen(r.id)}>
                    {r.status === "draft" && opts.can_raise ? "Edit" : "View"}
                  </button>
                  {r.status === "draft" && opts.can_raise && (
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => remove(r)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-3">
                  No purchase requests found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <span className="small text-muted">{count} request(s)</span>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <span className="small">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Purchase() {
  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState({ mode: "list", id: null });
  const [tab, setTab] = useState("request");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getPurchaseOptions()
      .then(setOpts)
      .catch((e) => setErr(e.message || "Could not load Purchase."));
  }, []);

  const backToList = () => {
    setView({ mode: "list", id: null });
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="container-fluid p-3" style={{ paddingBottom: 120 }}>
      <h4 className="mb-2">Purchase</h4>
      <ul className="nav nav-tabs mb-3">
        {TABS.filter((t) => !t.mastersOnly || (opts && opts.can_manage_masters)).map((t) => (
          <li className="nav-item" key={t.id}>
            <button
              type="button"
              className={"nav-link " + (!t.live ? "disabled" : tab === t.id ? "active" : "")}
              disabled={!t.live}
              title={t.live ? "" : "Coming soon"}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {!opts && !err && <div className="text-muted">Loading...</div>}

      {opts && tab === "request" && view.mode === "list" && (
        <RequestList
          opts={opts}
          refreshKey={refreshKey}
          onNew={() => setView({ mode: "form", id: null })}
          onOpen={(id) => setView({ mode: "form", id: id })}
        />
      )}
      {opts && tab === "request" && view.mode === "form" && (
        <RequestForm
          key={view.id || "new-" + refreshKey}
          opts={opts}
          requestId={view.id}
          onClose={backToList}
        />
      )}
      {opts && tab === "approve" && <ApproveTab opts={opts} />}
      {opts && tab === "invoice" && <TallyTab opts={opts} />}
      {opts && tab === "rates" && opts.can_manage_masters && <RateListTab opts={opts} />}
      {opts && tab === "vendors" && opts.can_manage_masters && <VendorsTab />}
    </div>
  );
}
